import { Schema, z } from 'zod/v3';
import { isStringWithOnlyNumbers } from '../../../common/utils';
import { zValidPeriod } from '../../../common/utils/schema';
import {
  DataObjectGroupType,
  DataObjectType,
  isDataObjectGroupType,
  isDataObjectType,
} from '../../../dataobject/types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserViewPermissionValidFormDataSchema.translations.json';

const { addTranslations, t } = i18nNsFactory('User');
addTranslations(translations);

const UserViewPermissionValidFormDataBaseSchema = z.object({
  validPeriod: zValidPeriod(),
});

const validateCustomIdWith =
  <T extends string>(typeGuard: (value: unknown) => value is T) =>
  (value: unknown): value is `${T}-${number}` => {
    if (typeof value !== 'string') {
      return false;
    }
    const [type, id] = value.split('-');
    return typeGuard(type) && isStringWithOnlyNumbers(id);
  };

/**
 * Accepting empty string but then not accepting it (in subsequent `refine` def) enables zod to properly report all validation errors.
 * Normally we would not define `.or(...).refine(...)` and catch missing/empty `id` value by defining errorMap to z.union
 * as second argument – `z.union([...], errorMap(() => t('...')))` – but currently this does not yield expected behaviour
 * and zod throws only this union error while any other validation errors are not thrown. We want to report all validation errors to user.
 * This is a workaround for known issue of zod: https://github.com/colinhacks/zod/issues/2558
 */
const matchButNotAcceptEmptyValue = <T>(schema: Schema<T>) =>
  schema.or(z.literal('')).refine(
    (value): value is T => Boolean(value),
    () => ({
      message: t('validation.viewPermissions.id.required'),
    }),
  );

export const UserViewPermissionValidFormDataSchema = z.union([
  UserViewPermissionValidFormDataBaseSchema.extend({
    id: matchButNotAcceptEmptyValue(
      z.custom<`${DataObjectGroupType}-${number}`>(
        validateCustomIdWith(isDataObjectGroupType),
      ),
    ),
  }),
  UserViewPermissionValidFormDataBaseSchema.extend({
    id: matchButNotAcceptEmptyValue(
      z.custom<`${DataObjectType.IN | DataObjectType.OUT}-${number}`>(
        validateCustomIdWith(isDataObjectType),
      ),
    ),
  }),
]);
